import { toast } from 'sonner';
import i18next from 'i18next';
import { hydrationManager } from '@/lib/services/reference-hydration.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

function refreshReferenceEntity(refKey) {
    void hydrationManager.refreshEntity(refKey, { force: true });
}

export class AssetLifecycleIntegration {
    /**
     * @param {*} licenseService - SDK SimpleCrudService for /asset-licenses
     * @param {*} disposalService - SDK SimpleCrudService for /asset-disposals
     * @param {*} eventService - SDK SimpleCrudService for /asset-lifecycle-events
     */
    constructor(licenseService, disposalService, eventService) {
        this.license = licenseService;
        this.disposal = disposalService;
        this.event = eventService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async listLicensesForAsset(assetId, params = {}) {
        try {
            const pageSize = params.pageSize ?? params.limit ?? 20;
            const r = await this.license.getAll({
                ...params,
                assetId,
                page: params.page,
                pageSize,
                limit: params.limit ?? pageSize,
                search: params.search,
                sortBy: params.sortBy,
                sortOrder: params.sortOrder,
                sortDirection: params.sortDirection ?? params.sortOrder,
            });
            return { success: true, data: r.data || [], pagination: r.pagination || {}, uniqueValues: r.uniqueValues || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [] };
        }
    }

    async listDisposalsForAsset(assetId, params = {}) {
        try {
            const pageSize = params.pageSize ?? params.limit ?? 20;
            const r = await this.disposal.getAll({
                ...params,
                assetId,
                page: params.page,
                pageSize,
                limit: params.limit ?? pageSize,
                search: params.search,
                sortBy: params.sortBy,
                sortOrder: params.sortOrder,
                sortDirection: params.sortDirection ?? params.sortOrder,
            });
            return { success: true, data: r.data || [], pagination: r.pagination || {}, uniqueValues: r.uniqueValues || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [] };
        }
    }

    async fetchLicense(id) {
        try {
            const r = await this.license.getById(id);
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: null };
        }
    }

    async fetchDisposal(id) {
        try {
            const r = await this.disposal.getById(id);
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: null };
        }
    }

    async listEventsForAsset(assetId, params = {}) {
        try {
            const r = await this.event.getAll({ ...params, assetId, limit: params.limit || 200 });
            return { success: true, data: r.data || [], pagination: r.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [] };
        }
    }

    async listAllLicenses(params = {}) {
        try {
            const r = await this.license.getAll(params);
            return {
                success: true,
                data: r.data || [],
                pagination: r.pagination || {},
                uniqueValues: r.uniqueValues || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [], uniqueValues: {} };
        }
    }

    async listAllDisposals(params = {}) {
        try {
            const r = await this.disposal.getAll(params);
            return {
                success: true,
                data: r.data || [],
                pagination: r.pagination || {},
                uniqueValues: r.uniqueValues || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [], uniqueValues: {} };
        }
    }

    async listAllLifecycleEvents(params = {}) {
        try {
            const { columnFilters, ...listParams } = params;
            const r = await this.event.getAll({
                populate: 'assetIds',
                ...listParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            return {
                success: true,
                data: r.data || [],
                pagination: r.pagination || {},
                uniqueValues: r.uniqueValues || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [], uniqueValues: {} };
        }
    }

    async fetchLicenseSummary() {
        try {
            const body = await this.license.api.getSubresource('/summary');
            return { success: true, data: body?.data ?? null };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: null };
        }
    }

    async fetchDisposalSummary() {
        try {
            const body = await this.disposal.api.getSubresource('/summary');
            return { success: true, data: body?.data ?? null };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: null };
        }
    }

    async createLicense(data) {
        try {
            const r = await this.license.create(data);
            toast.success(i18next.t('assets:licenseManagement.notifications.created', { defaultValue: 'License record created' }));
            refreshReferenceEntity('assetLicense');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async createDisposal(data) {
        try {
            const r = await this.disposal.create(data);
            toast.success(i18next.t('assets:disposalManagement.notifications.created', { defaultValue: 'Disposal record created' }));
            refreshReferenceEntity('assetDisposal');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateLicense(id, data, options = {}) {
        const { skipSuccessToast = false } = options;
        try {
            const r = await this.license.update(id, data);
            if (!skipSuccessToast) {
                toast.success(i18next.t('assets:licenseManagement.notifications.updated', { defaultValue: 'License record updated' }));
            }
            refreshReferenceEntity('assetLicense');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateDisposal(id, data, options = {}) {
        const { skipSuccessToast = false } = options;
        try {
            const r = await this.disposal.update(id, data);
            if (!skipSuccessToast) {
                toast.success(i18next.t('assets:disposalManagement.notifications.updated', { defaultValue: 'Disposal record updated' }));
            }
            refreshReferenceEntity('assetDisposal');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteLicense(id) {
        try {
            const r = await this.license.delete(id);
            toast.success(i18next.t('assets:licenseManagement.notifications.deleted', { defaultValue: 'License record deleted' }));
            refreshReferenceEntity('assetLicense');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteDisposal(id) {
        try {
            const r = await this.disposal.delete(id);
            toast.success(i18next.t('assets:disposalManagement.notifications.deleted', { defaultValue: 'Disposal record deleted' }));
            refreshReferenceEntity('assetDisposal');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async batchUpdateLicenses(ids, patch) {
        try {
            const r = await this.license.batchUpdate(ids, patch);
            if (r?.data?.failed?.length) {
                toast.warning(
                    i18next.t('assets:licenseManagement.batch.partialWarning', {
                        defaultValue: 'Some records could not be updated',
                    }),
                );
            } else {
                toast.success(i18next.t('assets:licenseManagement.notifications.batchUpdated', { defaultValue: 'License records updated' }));
            }
            refreshReferenceEntity('assetLicense');
            return { success: true, data: r.data, failedCount: r?.data?.failed?.length ?? 0 };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: null };
        }
    }

    async batchDeleteLicenses(ids) {
        try {
            const r = await this.license.batchDelete(ids);
            toast.success(i18next.t('assets:licenseManagement.notifications.batchDeleted', { defaultValue: 'Selected license records deleted' }));
            refreshReferenceEntity('assetLicense');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async batchDeleteDisposals(ids) {
        try {
            const r = await this.disposal.batchDelete(ids);
            toast.success(i18next.t('assets:disposalManagement.notifications.batchDeleted', { defaultValue: 'Selected disposal records deleted' }));
            refreshReferenceEntity('assetDisposal');
            return { success: true, data: r.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
